import { type IconNode } from "lucide-react";
import { createElement, forwardRef } from "react";

// alles von lucide kopiert

const defaultAttributes = {
  xmlns: "http://www.w3.org/2000/svg",
  width: 36,
  height: 24,
  viewBox: "0 0 37 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const hasA11yProp = (props: Record<string, unknown>) => {
  for (const prop in props) {
    if (prop.startsWith("aria-") || prop === "role" || prop === "title") {
      return true;
    }
  }
};

type IconProps = {
  color?: string;
  size?: number;
  strokeWidth?: number;
  absoluteStrokeWidth?: boolean;
  className?: string;
  iconNode: IconNode;
} & React.ComponentProps<"svg">;

const Icon = forwardRef<SVGSVGElement, IconProps>(
  (
    {
      color = "currentColor",
      size = 24,
      strokeWidth = 4,
      absoluteStrokeWidth,
      className = "",
      children,
      iconNode,
      ...rest
    },
    ref,
  ) =>
    createElement(
      "svg",
      {
        ref,
        ...defaultAttributes,
        width: size * 1.5,
        height: size,
        stroke: color,
        strokeWidth: absoluteStrokeWidth
          ? (Number(strokeWidth) * 24) / Number(size)
          : strokeWidth,
        className: className,
        ...(!children && !hasA11yProp(rest) && { "aria-hidden": "true" }),
        ...rest,
      },
      [
        ...iconNode.map(([tag, attrs]) => createElement(tag, attrs)),
        ...(Array.isArray(children) ? children : [children]),
      ],
    ),
);
Icon.displayName = "Icon";

const createIcon = (iconName: string, iconNode: IconNode) => {
  const Component = forwardRef<SVGSVGElement, Partial<IconProps>>(
    ({ className, ...props }, ref) =>
      createElement(Icon, {
        ref,
        iconNode: iconNode,
        className: className,
        ...props,
      }),
  );
  Component.displayName = iconName;
  return Component;
};

const tripleArrowsLeftIconNodes: IconNode = [
  ["path", { d: "m34 22-10-10 10-10", stroke: "#51b4ba", key: "1EkYAlg8" }],
  ["path", { d: "m23 22-10-10 10-10", stroke: "#51b4ba", key: "RDzAvt8T" }],
  ["path", { d: "m12 22-10-10 10-10", stroke: "#51b4ba", key: "8sm0LtTu" }],
];

const ArrowsTripleLeft = createIcon(
  "arrow-triple-left",
  tripleArrowsLeftIconNodes,
);

const tripleArrowsRightIconNodes: IconNode = [
  ["path", { d: "m24 22 10-10-10-10", stroke: "#51b4ba", key: "vhh8PA1W" }],
  ["path", { d: "m13 22 10-10-10-10", stroke: "#51b4ba", key: "61zSB9uF" }],
  ["path", { d: "m2 22 10-10-10-10", stroke: "#51b4ba", key: "Bv7FzqLI" }],
];

const ArrowsTripleRight = createIcon(
  "arrow-triple-right",
  tripleArrowsRightIconNodes,
);

export { ArrowsTripleLeft, ArrowsTripleRight };
